/**
 * Create, edit or view a store. Three columns: general information, ABC Dior
 * settings, MTM.
 */
import { useState, type SubmitEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ALLOWED_EXCLUSIVE_SYMBOLS } from '@/shared/constants';
import { appConfigQueryKey, fetchAppConfig } from '@/shared/config/app-config';
import { SelectField } from '@/shared/ui/SelectField';
import { Dialog } from '@/shared/ui/Dialog';
import {
  createStore,
  updateStore,
  workshopsQueryOptions,
  type StoreRecord,
} from './api';
import {
  COUNTRIES,
  emptyStoreForm,
  formToStore,
  fromDateTimeLocal,
  KEYBOARDS,
  REGIONS,
  storeToForm,
  toDateTimeLocal,
  validateStoreForm,
  type StoreFormErrors,
  type StoreFormValues,
} from './store-form';
import { optionsOf } from '@/shared/ui/select-options';
import { kebabCase } from '@/shared/lib/kebab-case';

export interface StoreDialogProps {
  item: StoreRecord | null;
  readOnly?: boolean;
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

const dialogTitle = (readOnly: boolean, creating: boolean): string => {
  if (readOnly) return 'Store info';
  return creating ? 'Store creation' : 'Store edit';
};

export function StoreDialog({
  item,
  readOnly = false,
  onClose,
}: Readonly<StoreDialogProps>) {
  const creating = !item;
  const queryClient = useQueryClient();
  const [values, setValues] = useState<StoreFormValues>(() =>
    item ? storeToForm(item) : emptyStoreForm(),
  );
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<StoreFormErrors>({});
  // Red border once a field has been edited and is invalid.
  const [dirty, setDirty] = useState<Set<keyof StoreFormValues>>(
    () => new Set(),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { data: workshops = [] } = useQuery(workshopsQueryOptions());
  const sortedWorkshops = [...workshops].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? ''),
  );

  const set = <K extends keyof StoreFormValues>(
    key: K,
    value: StoreFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setDirty((prev) => new Set(prev).add(key));
  };
  const liveErrors = validateStoreForm(values);
  const invalidClass = (
    key: keyof StoreFormValues,
    error: keyof StoreFormErrors | undefined,
  ) => (error && dirty.has(key) && liveErrors[error] ? 'invalid' : '');

  const save = useMutation({
    mutationFn: async () => {
      const data = formToStore(values);
      if (item) await updateStore({ ...data, id: item.id });
      else await createStore(data);
      await fetchAppConfig();
      await queryClient.invalidateQueries({ queryKey: appConfigQueryKey });
    },
    onSuccess: () => onClose('Confirm'),
    onError: () =>
      setErrorMessage(
        creating ? 'Failed to create store' : 'Failed to update store',
      ),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    const nextErrors = validateStoreForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  // The two by-pass options are mutually exclusive.
  const onBypassChanged = (
    key: 'bypassHeadOfficeApproval' | 'bypassBoutiqueManagerApproval',
    checked: boolean,
  ) =>
    setValues((prev) => ({
      ...prev,
      [key]: checked,
      [key === 'bypassHeadOfficeApproval'
        ? 'bypassBoutiqueManagerApproval'
        : 'bypassHeadOfficeApproval']: false,
    }));

  const title = dialogTitle(readOnly, creating);
  const fieldError = (key: keyof StoreFormErrors) => submitted && errors[key];
  const testId = (key: string) => `store-form-${kebabCase(key)}`;

  const text = (
    key: 'storeCode' | 'name' | 'email' | 'phone' | 'currency',
    label: string,
    placeholder: string,
    error?: keyof StoreFormErrors,
  ) => (
    <div className="mb-2">
      <label className="field-label" htmlFor={`store-${key}`}>
        {label}
      </label>
      <input
        id={`store-${key}`}
        data-testid={testId(key)}
        className={`field ${invalidClass(key, error)}`}
        name={key}
        placeholder={placeholder}
        value={values[key]}
        onChange={(event) => set(key, event.target.value)}
      />
      {error && (
        <small hidden={!fieldError(error)} data-testid={`${testId(key)}-error`}>
          {errors[error]}
        </small>
      )}
    </div>
  );

  const select = (
    key: 'country' | 'region' | 'workshopSav' | 'workshopEvent',
    label: string,
    placeholder: string,
    options: { value: string; label: string }[],
    error?: keyof StoreFormErrors,
  ) => (
    <SelectField
      id={`store-${key}`}
      name={key}
      label={label}
      placeholder={placeholder}
      options={options}
      value={values[key]}
      onChange={(value) => set(key, value)}
      className={key === 'region' || key === 'workshopEvent' ? 'mb-4' : 'mb-2'}
      fieldClassName={invalidClass(key, error)}
      error={error ? (errors[error] ?? '') : undefined}
      showError={!!error && !!fieldError(error)}
      testId={testId(key)}
    />
  );

  const checkbox = (
    id: string,
    label: ReactNode,
    checked: boolean,
    onChange: (checked: boolean) => void,
    indent = false,
    fieldTestId = testId(id),
  ) => (
    <div className={`mb-0 ${indent ? 'pl-4' : ''}`}>
      <div className="styled-input-square styled-input-single">
        <input
          type="checkbox"
          id={id}
          name={id}
          data-testid={fieldTestId}
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
        />
        <label htmlFor={id}>{label}</label>
      </div>
    </div>
  );

  const workshopOptions = sortedWorkshops.map((workshop) => ({
    value: workshop.id,
    label: workshop.name ?? '',
  }));

  return (
    <Dialog
      title={title}
      onClose={() => onClose('Cancel')}
      width="1200px"
      tall
      testId="store-dialog"
    >
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <fieldset disabled={readOnly} className="m-0 min-w-0 border-0 p-0">
          <div className="bs-row">
            <h3 className="col-span-3 flex justify-center">
              Store General Information
            </h3>
            <h3 className="col-span-3 flex justify-center">ABC Dior</h3>
            <h3 className="col-span-3 flex justify-center">MTM</h3>
          </div>

          <div className="bs-row">
            <div className="border-right col-span-3">
              {text('storeCode', 'Cegid code', 'Store code', 'storeCode')}
              {text('name', 'Store Name', 'Name', 'name')}
              <div className="mb-2">
                <label className="field-label" htmlFor="store-fullAddress">
                  Full address
                </label>
                <textarea
                  id="store-fullAddress"
                  data-testid="store-form-full-address"
                  className={`field ${invalidClass('fullAddress', 'fullAddress')}`}
                  name="fullAddress"
                  placeholder="Full address"
                  value={values.fullAddress}
                  onChange={(event) => set('fullAddress', event.target.value)}
                />
                <small
                  hidden={!fieldError('fullAddress')}
                  data-testid="store-form-full-address-error"
                >
                  {errors.fullAddress}
                </small>
              </div>
              {text('email', 'Email', 'Email', 'email')}
              {text('phone', 'Phone', 'Phone', 'phone')}
              {text('currency', 'Currency', 'currency')}
              {select(
                'country',
                'Country',
                '--Select country--',
                optionsOf(COUNTRIES),
              )}
              {select(
                'region',
                'Region',
                '--Select region--',
                optionsOf(REGIONS),
              )}
              {checkbox(
                'is-disabled',
                'Disable This Store',
                values.disabled,
                (v) => set('disabled', v),
                false,
                'store-form-disabled',
              )}
            </div>

            <div className="border-right col-span-3">
              <div className="mt-1">
                {checkbox(
                  'abcdiorActive',
                  'ABC Dior active',
                  values.abcdiorActive,
                  (v) => set('abcdiorActive', v),
                )}
              </div>
              {select(
                'workshopSav',
                'Workshop SAV',
                '--Select workshop--',
                workshopOptions,
                'workshopSav',
              )}
              {select(
                'workshopEvent',
                'Workshop Event',
                '--Select workshop--',
                workshopOptions,
              )}
              {values.workshopEvent && (
                <>
                  <div className="mt-4">
                    {checkbox(
                      'has-permanent-instore-workshop',
                      'Permanent In-store Workshop',
                      values.permanentInStoreWorkshop,
                      (v) => set('permanentInStoreWorkshop', v),
                      false,
                      'store-form-permanent-in-store-workshop',
                    )}
                  </div>
                  <div className="mb-2">
                    <label
                      className="field-label"
                      htmlFor="store-workshopEventStart"
                    >
                      Event Start Date
                    </label>
                    <input
                      id="store-workshopEventStart"
                      data-testid="store-form-workshop-event-start"
                      type="datetime-local"
                      className="field"
                      name="workshopEventStart"
                      placeholder="Event Start Date"
                      readOnly={values.permanentInStoreWorkshop}
                      value={toDateTimeLocal(values.workshopEventStart)}
                      onChange={(event) =>
                        set(
                          'workshopEventStart',
                          fromDateTimeLocal(event.target.value),
                        )
                      }
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      className="field-label"
                      htmlFor="store-workshopEventEnd"
                    >
                      Event End Date
                    </label>
                    <input
                      id="store-workshopEventEnd"
                      data-testid="store-form-workshop-event-end"
                      type="datetime-local"
                      className="field"
                      name="workshopEventEnd"
                      placeholder="Event End Date"
                      readOnly={values.permanentInStoreWorkshop}
                      value={toDateTimeLocal(values.workshopEventEnd)}
                      onChange={(event) =>
                        set(
                          'workshopEventEnd',
                          fromDateTimeLocal(event.target.value),
                        )
                      }
                    />
                  </div>
                </>
              )}
              {checkbox(
                'is-stock-control-enabled',
                'Stock Control Active',
                values.stockControl,
                (v) => set('stockControl', v),
                false,
                'store-form-stock-control',
              )}
              {checkbox(
                'bypassHeadOfficeApproval',
                'By-pass head-office approval',
                values.bypassHeadOfficeApproval,
                (v) => onBypassChanged('bypassHeadOfficeApproval', v),
              )}
              {checkbox(
                'bypassBoutiqueManagerApproval',
                'By-pass boutique manager approval',
                values.bypassBoutiqueManagerApproval,
                (v) => onBypassChanged('bypassBoutiqueManagerApproval', v),
              )}
              {checkbox(
                'is-central-flow-forbidden',
                'Central Flow Forbidden',
                values.centralFlowForbidden,
                (v) => set('centralFlowForbidden', v),
                false,
                'store-form-central-flow-forbidden',
              )}
              {checkbox(
                'iosKeyboardActive',
                'Active iOS Keyboard',
                values.iosKeyboardActive,
                (v) => set('iosKeyboardActive', v),
              )}
              {values.iosKeyboardActive &&
                KEYBOARDS.map((keyboard) =>
                  checkbox(
                    `${keyboard.key}KeyboardActive`,
                    keyboard.label,
                    values.keyboards[keyboard.key],
                    (v) =>
                      set('keyboards', {
                        ...values.keyboards,
                        [keyboard.key]: v,
                      }),
                    true,
                    testId(`keyboard-${keyboard.key}`),
                  ),
                )}
              {checkbox(
                'symbolActive',
                'Active Iconic Symbols',
                values.symbolActive,
                (v) => set('symbolActive', v),
              )}
              {checkbox(
                'exclusiveSymbolActive',
                'Active exclusive Symbols',
                values.exclusiveSymbolActive,
                (v) => set('exclusiveSymbolActive', v),
              )}
              {values.exclusiveSymbolActive &&
                ALLOWED_EXCLUSIVE_SYMBOLS.map((symbol) =>
                  checkbox(
                    symbol.id,
                    <>
                      <span
                        className={`symbol-${symbol.code}`}
                        aria-hidden="true"
                      />{' '}
                      {symbol.value}
                    </>,
                    values.exclusiveSymbols[symbol.key],
                    (v) =>
                      set('exclusiveSymbols', {
                        ...values.exclusiveSymbols,
                        [symbol.key]: v,
                      }),
                    true,
                    testId(`exclusive-symbol-${symbol.key}`),
                  ),
                )}
              {checkbox(
                'iconicThreadsActive',
                'Active Iconic Threads',
                values.iconicThreadsActive,
                (v) => set('iconicThreadsActive', v),
              )}
              {checkbox(
                'exclusiveThreadsActive',
                'Active Exclusive Threads',
                values.exclusiveThreadsActive,
                (v) => set('exclusiveThreadsActive', v),
              )}
            </div>

            <div className="col-span-3">
              <div className="mt-1">
                {checkbox('dmsActive', 'MTM active', values.dmsActive, (v) =>
                  set('dmsActive', v),
                )}
              </div>
            </div>
          </div>

          {errorMessage && (
            <div
              className="alert mb-0"
              role="alert"
              data-testid="store-form-error"
            >
              <span className="error-msg">{errorMessage}</span>
            </div>
          )}
          {!readOnly && (
            <div className="mt-6 flex justify-center">
              <button
                className="button button-form"
                type="submit"
                disabled={save.isPending}
                data-testid="store-form-save-button"
              >
                Save
              </button>
            </div>
          )}
        </fieldset>
      </form>
    </Dialog>
  );
}
